import {ToggleButtonGroup} from '@wandb/components';
import {Select} from '@wandb/weave/components/Form/Select';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React from 'react';
import {useShallow} from 'zustand/react/shallow';

import {capitalizeFirstLetter} from '../../../../util/string';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {MetricNameInput} from './MetricNameInput';
import {RunFilterChunk} from './RunFilterShared';
import {
  type ChangeDirectionOption,
  changeDirectionOptions,
  changeTypeOptions,
  getChangeDirectionOption,
  getChangeTypeOptionForEnumValue,
  getChangeTypeOptionForToggleValue,
  getRunMetricChangeString,
  isValidChangeAmount,
  isValidMetricName,
  isValidWindowSize,
  RunMetricChangeActions,
  useRunMetricChangeStore,
} from './RunMetricChangeStore';
import {type AggOpOption, aggOpOptions} from './RunMetricThresholdStore';
import {BoldLabel, ErrorText, SeparatedChunk} from './shared';

// Re-export types and functions from store for backwards compatibility
export type {
  ChangeDirection,
  ChangeDirectionOption,
  ChangeTypeOption,
  MetricChangeFilter,
  RunMetricChangeConfig,
  RunMetricChangeType,
} from './RunMetricChangeStore';
export {
  changeDirectionOptions,
  changeTypeOptions,
  getChangeDirectionOption,
  getChangeTypeOptionForEnumValue,
  getChangeTypeOptionForToggleValue,
  getDirectionAndAmountString,
  getEventConfigForRunMetricChange,
  getRunMetricChangeConfig,
  getRunMetricChangeString,
  isValidChangeAmount,
  isValidChangeDirection,
  isValidChangeType,
  isValidRunMetricChangeConfig,
  RunMetricChangeActions,
  useRunMetricChangeStore,
} from './RunMetricChangeStore';

export const RunMetricChangeSection = () => {
  const metricFilter = useRunMetricChangeStore(
    useShallow(state => state.metricFilter)
  );
  const runFilter = useRunMetricChangeStore(
    useShallow(state => state.runFilter)
  );

  // the undefined checks here prevent showing the error before user has configured that field
  const showMetricNameError =
    metricFilter.metricName !== undefined &&
    !isValidMetricName(metricFilter.metricName);
  const showPriorSizeError =
    metricFilter.priorSize !== undefined &&
    !isValidWindowSize(metricFilter.priorSize);
  const showCurrentSizeError =
    metricFilter.currentSize !== undefined &&
    !isValidWindowSize(metricFilter.currentSize);

  const eventDescription = capitalizeFirstLetter(
    getRunMetricChangeString(metricFilter, runFilter)
  );

  const currentAggOpOption =
    aggOpOptions.find(option => option.value === metricFilter.aggOp) ?? null; // note that null tells react-select to show the placeholder, undefined will not

  return (
    <Tailwind>
      <div className="mt-16 font-semibold">Metric setup</div>
      <div className="mt-8 flex flex-col gap-4 rounded-lg border border-wb-border p-16">
        <S.AutomationDescriptionSection>
          <S.AutomationDescriptionIcon>
            <Icon name="recent-clock" />
          </S.AutomationDescriptionIcon>
          <S.AutomationDescriptionBody>
            <S.AutomationDescriptionTitle>
              In the event that
            </S.AutomationDescriptionTitle>
            <S.AutomationDescriptionText>
              {eventDescription}
            </S.AutomationDescriptionText>
          </S.AutomationDescriptionBody>
        </S.AutomationDescriptionSection>
        <SeparatedChunk>
          <BoldLabel>Metric</BoldLabel>
          <div className="mt-4">
            <MetricNameInput
              value={metricFilter.metricName}
              onChange={value => RunMetricChangeActions.setMetricName(value)}
            />
            {showMetricNameError && (
              <ErrorText>Metric name cannot be empty</ErrorText>
            )}
          </div>
        </SeparatedChunk>
        <SeparatedChunk>
          <BoldLabel>
            <div className="flex items-center">
              Current window
              <div className="ml-4">
                <Tooltip
                  content="Integer 1 - 100 required."
                  trigger={
                    <Icon
                      className="inline-block text-moon-400 dark:text-wb-text-tertiary"
                      name="info"
                    />
                  }
                />
              </div>
            </div>
          </BoldLabel>
          <div className="mt-4 flex items-center">
            <Select<AggOpOption>
              options={aggOpOptions}
              placeholder="Select an aggregation operation..."
              value={currentAggOpOption}
              onChange={(aggOp: AggOpOption | null) => {
                if (aggOp) {
                  RunMetricChangeActions.setAggOp(aggOp.value);
                }
              }}
              autoFocus={true}
              className="mr-8"
            />{' '}
            of
            <span className="mx-8 inline-block w-64">
              <TextField
                type="number"
                value={metricFilter.currentSize.toString()}
                onChange={value => {
                  RunMetricChangeActions.setCurrentSize(value);
                }}
                placeholder="Ex. 5"
                errorState={showCurrentSizeError}
              />
            </span>
            logged values
          </div>
          {showCurrentSizeError && (
            <ErrorText>Integer 1 - 100 required</ErrorText>
          )}
        </SeparatedChunk>
        <SeparatedChunk>
          <BoldLabel>
            <div className="flex items-center">
              Prior window
              <div className="ml-4">
                <Tooltip
                  content="Integer 1 - 100 required."
                  trigger={
                    <Icon
                      className="inline-block text-moon-400 dark:text-wb-text-tertiary"
                      name="info"
                    />
                  }
                />
              </div>
            </div>
          </BoldLabel>
          <div className="mt-4 flex items-center">
            <Select<AggOpOption>
              options={aggOpOptions}
              placeholder="Select an aggregation operation..."
              value={currentAggOpOption}
              onChange={(aggOp: AggOpOption | null) => {
                if (aggOp) {
                  RunMetricChangeActions.setAggOp(aggOp.value);
                }
              }}
              autoFocus={true}
              className="mr-8"
            />{' '}
            of
            <span className="mx-8 inline-block w-64">
              <TextField
                type="number"
                value={metricFilter.priorSize.toString()}
                onChange={value => {
                  RunMetricChangeActions.setPriorSize(value);
                }}
                placeholder="Ex. 5"
                errorState={showPriorSizeError}
              />
            </span>
            logged values prior to the current window
          </div>
          {showPriorSizeError && (
            <ErrorText>Integer 1 - 100 required</ErrorText>
          )}
        </SeparatedChunk>
        <MetricThresholdConfig />
        <SeparatedChunk>
          <RunFilterChunk
            runFilter={runFilter}
            actions={RunMetricChangeActions}
          />
        </SeparatedChunk>
      </div>
    </Tailwind>
  );
};

export const MetricThresholdConfig = () => {
  const metricFilter = useRunMetricChangeStore(
    useShallow(state => state.metricFilter)
  );
  const showChangeAmountError =
    metricFilter.changeAmount !== undefined &&
    !isValidChangeAmount(metricFilter.changeAmount);
  return (
    <>
      <SeparatedChunk>
        <div className="mb-8 mt-4 flex flex-row gap-16">
          <div className="flex-1 font-semibold">Metric threshold</div>
          <ToggleButtonGroup
            aria-label="Metric change type"
            items={changeTypeOptions.map(option => ({
              id: option.value,
              children: option.value,
            }))}
            selectedKeys={[
              getChangeTypeOptionForEnumValue(metricFilter.changeType)!.value,
            ]}
            selectionMode="single"
            disallowEmptySelection
            size="sm"
            onSelectionChange={keys => {
              const option = getChangeTypeOptionForToggleValue(
                String(Array.from(keys)[0])
              );
              if (option) {
                RunMetricChangeActions.setChangeType(option.enumValue);
              }
            }}
          />
        </div>

        <div className="mt-4 flex flex-row gap-16">
          <div className="flex-2">
            <Select<ChangeDirectionOption>
              value={getChangeDirectionOption(metricFilter.changeDirection)}
              onChange={e =>
                RunMetricChangeActions.setChangeDirection(
                  e?.value || 'INCREASE'
                )
              }
              options={changeDirectionOptions}
            />
          </div>
          <div className="flex-1">
            <TextField
              value={metricFilter.changeAmount || ''}
              onChange={value => RunMetricChangeActions.setChangeAmount(value)}
              placeholder="Ex. 0.9"
              errorState={showChangeAmountError}
            />
          </div>
          {metricFilter.changeType === 'RELATIVE' && (
            <div className="flex-0">%</div>
          )}
        </div>
        {showChangeAmountError && (
          <S.ErrorText>Change amount must be a valid number</S.ErrorText>
        )}
      </SeparatedChunk>
    </>
  );
};
